import { FC, PropsWithChildren, ReactNode, useContext, useEffect } from 'react'
import { Box, Container, GlobalStyles, Paper, SxProps } from '@mui/material'
import { tss } from '@dhl/theme'
import { useLocalStorage } from '@dhl/use-storage'
import { VersionContext } from '@dhl/version-provider'

import { BottomNavigation } from './BottomNavigation.js'
import { SideMenu } from './SideMenu.js'
import { AppHeader } from './AppHeader.js'
import { MenuItemWithSubItems, PageMaxWidth } from './types.js'
import { Loading } from './Loading.js'

export type PageProps = {
  breadcrumbs?: ReactNode[]
  classes?: Partial<Record<'root' | 'content' | 'appHeader', string>>
  disableTab?: boolean
  drawerWidth?: number
  headerBackground?: string
  headerContent?: ReactNode
  headerExtraToolBar?: ReactNode
  headerInformation?: ReactNode
  hideContentWhileLoading?: boolean
  hideHeader?: boolean
  hideMenu?: boolean
  logoHref?: string
  inlineSubMenuItems?: boolean
  isDefaultExpanded?: boolean
  isDesktop?: boolean
  isFocus?: boolean
  isLoading?: boolean
  useAutomaticMoreInMenu?: boolean
  sx?: SxProps

  /**
   * This flag 'isReloadable' only works when you are consuming the VersionProvider in your app,
   * else our component doesn't know if there is a new version available or not. Please check your
   * implementation when using it!
   */
  isReloadable?: boolean
  maxWidth?: PageMaxWidth

  /**
   * This prop allows you to inject custom content into the starting section of the sidebar, ensuring it appears above all other sidebar elements.
   */
  SideMenuStartComponent?: ReactNode

  /**
   * This prop allows you to inject custom content into the ending section of the sidebar, ensuring it appears above all other sidebar elements.
   */
  SideMenuEndComponent?: ReactNode

  menuItems?: MenuItemWithSubItems[]
  navigationPath?: string
  testId?: string
  withoutRouter?: boolean

  /**
   * Is the application running on acceptance or not.
   * We mostly deploy to ACC and PRD (also a preview one but edge case), and want to visually show this to our enduser.
   *
   * @default: checks wheter there is `accept` in your hostname, this is a client-side solution so
   * best is to pass your own. Some apps give an env `ENVIRONMENT` in their k8s, which is the most reliable one.
   */
  isAccept?: () => boolean

  /**
   * When you pass the isAccept function but still do not want the chip to tell so because of avilable space (mobile app)
   * you can overrule this. Make sure to then set `showAcceptHeader` so we still have some visible recognition (grey header)
   *
   * @default true
   */
  showAcceptChip?: boolean

  /**
   * When you pass the isAccept function but want to show this only by another header color turn this on
   *
   * @default false
   */
  showAcceptHeader?: boolean

  /**
   * You can have influence on the i18n text in the acceptance chip when its on
   * @default 'Acceptance'
   */
  acceptLabel?: string

  /**
   * Internal option for the PageWithRefresh composition to not clash with loading states
   */
  isRefreshing?: boolean

  /**
   * When you do not want the inline padding on the top and left and right and define
   * your own rules. The usecase is when you want to show a Hero banner on your page which visually
   * takes full width.
   */
  isFullWidth?: boolean

  /**
   * removes padding on the container
   */
  disablePadding?: boolean

  /**
   * At the moment everyting is controlled with `classes` but thats deprecated.
   * When you want to use all available screenspace use this option!
   */
  useFullSize?: boolean

  /**
   * Style of submenu where items are collapsed by default
   */
  collapsedSubmenu?: boolean

  /** @deprecated: was not used anywhere, will be phased out */
  // eslint-disable-next-line react/no-unused-prop-types
  isDense?: boolean

  /** @deprecated: was not used anywhere, will be phased out */
  // eslint-disable-next-line react/no-unused-prop-types
  isFlexContent?: boolean

  /** @deprecated: was not used anywhere, will be phased out */
  // eslint-disable-next-line react/no-unused-prop-types
  spacing?: number
}

type MakeStylesProps = Pick<PageProps, 'isDesktop' | 'isFullWidth'>

export const isNavigationExpandedKey = 'isNavigationExpanded'

const useStyles = tss.makeStyles<MakeStylesProps>()((theme, { isDesktop, isFullWidth }) => ({
  content: {
    flexGrow: 1,
    paddingTop: isFullWidth ? 0 : theme.spacing(2),
    paddingLeft: isFullWidth ? 0 : theme.spacing(isDesktop ? 5 : 2),
    paddingRight: isFullWidth ? 0 : theme.spacing(isDesktop ? 5 : 2),
    paddingBottom: theme.spacing(isDesktop ? 2 : 10),
    '@media print': {
      padding: 0,
    },
  },
  bottomNavigation: {
    bottom: 0,
    width: '100%',
    position: 'fixed',
    zIndex: 1100,
  },
}))

/**
 * Central DHL Page component with option to have breadcrumbs, sidemenu and some extra header compositions.
 * Please be aware if your Page needs a pull-to-refresh mechanism we have the `PageWithRefresh` component
 */
export const Page: FC<PropsWithChildren<PageProps>> = ({
  breadcrumbs,
  children,
  classes,
  collapsedSubmenu = false,
  disableTab = false,
  drawerWidth = 180,
  headerBackground,
  headerContent,
  headerExtraToolBar,
  headerInformation,
  hideContentWhileLoading,
  hideHeader = false,
  hideMenu = false,
  logoHref,
  inlineSubMenuItems = false,
  isDefaultExpanded = true,
  isDesktop = false,
  isFocus = false,
  isFullWidth = false,
  isLoading = false,
  disablePadding = false,
  useAutomaticMoreInMenu = false,
  isReloadable = false,
  isRefreshing = false,
  maxWidth = 'md',
  SideMenuStartComponent,
  SideMenuEndComponent,
  menuItems,
  navigationPath,
  testId,
  withoutRouter = false,
  isAccept,
  showAcceptChip,
  showAcceptHeader,
  acceptLabel,
  useFullSize,
  sx,
}) => {
  const [isExpanded, setIsExpanded] = useLocalStorage<boolean>(isNavigationExpandedKey, isDefaultExpanded)
  const { setIsReloadable } = useContext(VersionContext)

  const { classes: styles, cx } = useStyles({
    isDesktop,
    isFullWidth,
  })

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    setIsReloadable(isReloadable)
  }, [setIsReloadable, isReloadable])

  const toggle = () => setIsExpanded(!isExpanded)

  const showLoading = isLoading && !isRefreshing
  const showContent = !isLoading || !hideContentWhileLoading || isRefreshing

  return (
    <Box
      data-test={!isLoading && testId}
      data-testid={testId}
      className={classes?.root}
      sx={{
        ...(useFullSize
          ? {
              width: '100vw',
              height: '100vh',
              overflowX: 'hidden',
              overflowY: 'auto',
            }
          : {}),
        ...sx,
      }}
    >
      <GlobalStyles
        styles={(theme) => ({
          body: {
            backgroundColor: isFocus ? theme.palette.common.white : theme.palette.background.default,
            [theme.breakpoints.down('md')]: {
              width: '100%',
            },
          },
          '#root': {
            backgroundColor: isFocus ? theme.palette.common.white : theme.palette.background.default,
            overflowY: 'auto',
            scrollBehavior: 'smooth',
            [theme.breakpoints.down('md')]: {
              overscrollBehavior: 'contain',
              height: ['100%', '-webkit-fill-available'],
              width: '100%',
            },
          },
        })}
      />
      {!hideHeader && (
        <AppHeader
          headerContent={headerContent}
          breadcrumbs={isDesktop ? breadcrumbs : []}
          headerBackground={headerBackground}
          headerExtraToolBar={headerExtraToolBar}
          headerInformation={headerInformation}
          logoHref={logoHref}
          position="sticky"
          drawerWidth={drawerWidth}
          withoutRouter={withoutRouter}
          className={classes?.appHeader}
          isAccept={isAccept}
          showAcceptChip={showAcceptChip}
          showAcceptHeader={showAcceptHeader}
          acceptLabel={acceptLabel}
        />
      )}
      <Box
        display="flex"
        flex={1}
        sx={{
          ...// eslint-disable-next-line no-nested-ternary
          (useFullSize ? (hideHeader ? { height: '100%' } : { height: 'calc(100% - 48px)' }) : {}),
        }}
      >
        {menuItems && isDesktop && !hideMenu && (
          <SideMenu
            collapsedSubmenu={collapsedSubmenu}
            disableTab={disableTab}
            drawerWidth={drawerWidth}
            hasInformationBar={Boolean(headerInformation)}
            inlineSubMenuItems={inlineSubMenuItems}
            isExpanded={isExpanded}
            menuItems={menuItems}
            navigationPath={navigationPath}
            StartComponent={SideMenuStartComponent}
            EndComponent={SideMenuEndComponent}
            toggleExpanded={toggle}
          />
        )}

        {menuItems && menuItems.length > 0 && !isDesktop && !hideMenu && (
          <Paper elevation={4} className={styles.bottomNavigation}>
            <BottomNavigation
              inlineSubMenuItems={inlineSubMenuItems}
              menuItems={menuItems}
              useAutomaticMoreInMenu={useAutomaticMoreInMenu}
            />
          </Paper>
        )}
        <Container
          maxWidth={disablePadding ? false : maxWidth}
          disableGutters={disablePadding}
          data-test="page-content"
          id="page-content"
          className={cx(styles.content, classes?.content)}
        >
          {showLoading && <Loading isMiddle />}
          {showContent ? children : null}
        </Container>
      </Box>
    </Box>
  )
}
